import { AnimatePresence, Variants, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { useState } from "react";
import useSWR from "swr";
import { TrashIcon } from "@heroicons/react/24/outline";
import { fetcher } from "@utils/fetchers";
import { joinClassNames } from "@utils/helpers";
import { useLatestCookieValue, useWindowSize } from "@utils/hooks";
import { useIsShopsite } from "@context/DomainContext";
import { postDeleteCombos } from "@features/cartDetails/utils/deleteTicketsUtil";
import { CheckoutDetails } from "@features/checkout/types";
import { Analytics } from "@types";
import { useNotificationStore } from "@store/notificationStore";
import LoadingSpinner from "@components/loadingSpinner";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/popovers/dialog";
import DialogCloseButton from "@components/popovers/dialogCloseButton";
import { Button, Card, Heading, Text } from "@components/theming";

interface Props {
  uniqueId: number;
  is_last_combo?: boolean;
  eventId: number;
  showingId: number;
  eventName: string;
  priceAgeId: number;
  type: string;
  price: number;
  analyticsConfigs?: Analytics[];
  canTrack?: boolean;
}

const DeleteCombo = ({
  uniqueId,
  is_last_combo = false,
  eventId,
  showingId,
  eventName,
  priceAgeId,
  type,
  price,
  analyticsConfigs,
  canTrack,
}: Props) => {
  const windowSize = useWindowSize();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const [open, setOpen] = useState(false);
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const [loading, setLoading] = useState(false);
  const deleteTranslations = useTranslations(
    "Checkout.Cart.tickets.combos.delete"
  );
  const isShopsite = useIsShopsite();

  const { data: checkoutDetails } = useSWR<CheckoutDetails>(
    basketKey ? `basket/${basketKey}/checkout` : null,
    fetcher
  );

  const handleDeleteTicket = async () => {
    setLoading(true);

    await postDeleteCombos(
      uniqueId,
      is_last_combo,
      setSuccess,
      setFailure,
      eventId,
      showingId,
      priceAgeId,
      eventName,
      type,
      price,
      checkoutDetails?.Currency.CurrencyCode ?? "",
      analyticsConfigs,
      canTrack,
      deleteTranslations
    );

    setLoading(false);
  };

  const variants: Variants =
    windowSize.width && windowSize.width >= 640
      ? {
          hidden: {
            opacity: 0,
            scale: 0.9,
            transition: { duration: 0.125 },
          },
          show: {
            opacity: 1,
            scale: 1,
          },
        }
      : {
          hidden: {
            y: 200,
            opacity: 0,
          },
          show: {
            y: 0,
            opacity: 1,
          },
        };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={loading || checkoutDetails?.IsPended} asChild>
        <button
          type="button"
          aria-label={deleteTranslations("removeticket")}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="w-fit text-base font-medium text-white"
        >
          {loading ? (
            <LoadingSpinner
              size="20"
              color={!isShopsite ? "var(--gray-11)" : undefined}
            />
          ) : (
            <TrashIcon
              aria-hidden="true"
              className={joinClassNames(
                "w-5 h-5 sm:w-6 sm:h-6",
                checkoutDetails?.IsPended
                  ? isShopsite
                    ? "text-red-200 cursor-not-allowed"
                    : "text-[color:var(--red-a6)] cursor-not-allowed"
                  : isShopsite
                  ? "text-red-500"
                  : "text-[color:var(--red-9)]"
              )}
            />
          )}
        </button>
      </DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent
            asChild
            forceMount
            handleClose={(e: any) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            <Card asChild shopsiteClasses="rounded-3xl overflow-hidden">
              <motion.div
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={variants}
                transition={{ duration: 0.25 }}
                className="flex flex-col"
              >
                <div
                  className={joinClassNames(
                    "flex flex-col gap-4 pb-4",
                    isShopsite ? "px-6 pt-6 gap-4 bg-red-500" : ""
                  )}
                >
                  <div className="relative self-end">
                    <DialogClose onClick={(e) => e.stopPropagation()} asChild>
                      <DialogCloseButton shopsiteVariant="white" />
                    </DialogClose>
                  </div>
                  <DialogHeader className="!text-left">
                    <DialogTitle asChild>
                      <Heading
                        as="h3"
                        size="5"
                        weight="bold"
                        shopsiteClasses="text-white text-xl font-bold"
                      >
                        {deleteTranslations("heading")}
                      </Heading>
                    </DialogTitle>
                    <TrashIcon
                      className={joinClassNames(
                        "absolute top-0 left-4 w-24 h-24 -rotate-12",
                        isShopsite
                          ? "text-white/10"
                          : " text-[color:var(--red-a3)]"
                      )}
                    />
                  </DialogHeader>
                </div>
                <Text color="gray" shopsiteClasses="text-gray-600 px-6 pt-4">
                  {deleteTranslations("description")}
                </Text>
                <div
                  className={joinClassNames(
                    "flex-1 flex items-center justify-between gap-6 mt-4",
                    isShopsite ? "px-6 pb-6" : ""
                  )}
                >
                  <DialogClose onClick={(e) => e.stopPropagation()} asChild>
                    <Button
                      shopsiteClasses="!bg-gray-100 hover:!bg-gray-500 !text-gray-800 hover:!text-white"
                      color="gray"
                      size="3"
                      sharedClasses="grow"
                    >
                      {deleteTranslations("cancel")}
                    </Button>
                  </DialogClose>
                  <DialogClose
                    asChild
                    onClick={async (e) => {
                      e.stopPropagation();
                      await handleDeleteTicket();
                    }}
                  >
                    <Button
                      color="red"
                      size="3"
                      sharedClasses="grow"
                      shopsiteClasses="bg-red-500 hover:bg-red-400 text-white"
                    >
                      {deleteTranslations("delete")}
                    </Button>
                  </DialogClose>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default DeleteCombo;
